import React, { useState, useEffect } from "react";
import { collection, getDocs, addDoc, updateDoc, doc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import {
    Beer,
    Plus,
    Edit2,
    Trash2,
    CheckSquare,
    Square,
    Save,
    X,
    ChefHat,
    Utensils,
    Coffee,
    ShoppingBag,
    Wine,
    TrendingUp,
    Package,
    DollarSign,
    Eye,
    EyeOff
} from "lucide-react";

const Dashboard = () => {
    const [allItems, setAllItems] = useState({});
    const [newItem, setNewItem] = useState({
        category: "",
        name: "",
        price: "",
        weight: "",
        description: "",
        prices: { "200ml": "", "400ml": "", lata: "" },
        brand: "",
        alcohol: "",
        type: "",
        stock: "",
        size: "",
        quantity: "",
        active: true,
    });
    const [editItemId, setEditItemId] = useState(null);
    const [editCategory, setEditCategory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const categories = [
        { name: "Entradas", collection: "entradas", icon: ChefHat, color: "bg-orange-100 border-orange-300" },
        { name: "Fuertes", collection: "fuertes", icon: Utensils, color: "bg-red-100 border-red-300" },
        { name: "Extras", collection: "extras", icon: Plus, color: "bg-yellow-100 border-yellow-300" },
        { name: "Postres", collection: "postres", icon: Coffee, color: "bg-pink-100 border-pink-300" },
        { name: "Merch", collection: "merch", icon: ShoppingBag, color: "bg-purple-100 border-purple-300" },
        { name: "Bebidas", collection: "bebidas", icon: Wine, color: "bg-blue-100 border-blue-300" },
        { name: "Cervezas", collection: "beers", icon: Beer, color: "bg-amber-100 border-amber-300" }
    ];

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const itemsData = {};

            // Fetch items from each category collection
            for (const category of categories) {
                const collectionRef = collection(db, "Restaurants", "AlmacenGDL", category.collection);
                const snapshot = await getDocs(collectionRef);

                itemsData[category.name] = snapshot.docs.map(doc => ({
                    id: doc.id,
                    category: category.name,
                    ...doc.data(),
                    active: doc.data().active !== undefined ? doc.data().active : true
                }));

                // Sort beers by ID
                if (category.name === "Cervezas") {
                    itemsData[category.name].sort((a, b) => {
                        const aId = isNaN(a.id) ? parseInt(a.id) || 0 : a.id;
                        const bId = isNaN(b.id) ? parseInt(b.id) || 0 : b.id;
                        return aId - bId;
                    });
                }
            }

            setAllItems(itemsData);
        } catch (err) {
            setError("Failed to fetch data: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const getCollectionName = (categoryName) => {
        const category = categories.find(cat => cat.name === categoryName);
        return category ? category.collection : categoryName.toLowerCase();
    };

    const handleAddItem = async (e) => {
        e.preventDefault();
        try {
            const collectionName = getCollectionName(newItem.category);
            const collectionRef = collection(db, "Restaurants", "AlmacenGDL", collectionName);

            const itemData = {
                name: newItem.name,
                active: newItem.active,
                category: newItem.category,
                timestamp: serverTimestamp(),
            };

            if (newItem.category === "Cervezas") {
                itemData.brand = newItem.brand;
                itemData.alcohol = newItem.alcohol;
                itemData.type = newItem.type || "";
                itemData.prices = {
                    "200ml": newItem.prices["200ml"] ? parseFloat(newItem.prices["200ml"]) : null,
                    "400ml": newItem.prices["400ml"] ? parseFloat(newItem.prices["400ml"]) : null,
                    "lata": newItem.prices.lata ? parseFloat(newItem.prices.lata) : null
                };
                // Remove null values
                Object.keys(itemData.prices).forEach(key => {
                    if (itemData.prices[key] === null) {
                        delete itemData.prices[key];
                    }
                });
            } else {
                itemData.price = parseFloat(newItem.price) || 0;
                if (newItem.weight) itemData.weight = newItem.weight;
                if (newItem.size) itemData.size = newItem.size;
                if (newItem.quantity) itemData.quantity = newItem.quantity;
                if (newItem.description) itemData.description = newItem.description;
                if (newItem.category === "Merch" && newItem.stock) {
                    itemData.stock = parseInt(newItem.stock) || 0;
                }
            }

            await addDoc(collectionRef, itemData);

            setNewItem({
                category: "",
                name: "",
                price: "",
                weight: "",
                description: "",
                prices: { "200ml": "", "400ml": "", lata: "" },
                brand: "",
                alcohol: "",
                type: "",
                stock: "",
                size: "",
                quantity: "",
                active: true,
            });

            fetchData();
        } catch (err) {
            setError("Failed to add item: " + err.message);
        }
    };

    const handleEditItem = (item) => {
        setEditItemId(item.id);
        setEditCategory(item.category);
        setNewItem({
            category: item.category,
            name: item.name || "",
            price: item.price || "",
            weight: item.weight || "",
            description: item.description || "",
            prices: item.prices || { "200ml": "", "400ml": "", lata: "" },
            brand: item.brand || "",
            alcohol: item.alcohol || "",
            type: item.type || "",
            stock: item.stock || "",
            size: item.size || "",
            quantity: item.quantity || "",
            active: item.active !== undefined ? item.active : true,
        });
    };

    const handleSaveEdit = async () => {
        try {
            const collectionName = getCollectionName(editCategory);
            const docRef = doc(db, "Restaurants", "AlmacenGDL", collectionName, editItemId);

            const updateData = {
                name: newItem.name,
                active: newItem.active,
                timestamp: serverTimestamp(),
            };

            if (editCategory === "Cervezas") {
                updateData.brand = newItem.brand;
                updateData.alcohol = newItem.alcohol;
                updateData.type = newItem.type || "";
                updateData.prices = {
                    "200ml": newItem.prices["200ml"] ? parseFloat(newItem.prices["200ml"]) : null,
                    "400ml": newItem.prices["400ml"] ? parseFloat(newItem.prices["400ml"]) : null,
                    "lata": newItem.prices.lata ? parseFloat(newItem.prices.lata) : null
                };
                // Remove null values
                Object.keys(updateData.prices).forEach(key => {
                    if (updateData.prices[key] === null) {
                        delete updateData.prices[key];
                    }
                });
            } else {
                updateData.price = parseFloat(newItem.price) || 0;
                if (newItem.weight) updateData.weight = newItem.weight;
                if (newItem.size) updateData.size = newItem.size;
                if (newItem.quantity) updateData.quantity = newItem.quantity;
                if (newItem.description) updateData.description = newItem.description;
                if (editCategory === "Merch" && newItem.stock) {
                    updateData.stock = parseInt(newItem.stock) || 0;
                }
            }

            await updateDoc(docRef, updateData);

            setEditItemId(null);
            setEditCategory(null);
            setNewItem({
                category: "",
                name: "",
                price: "",
                weight: "",
                description: "",
                prices: { "200ml": "", "400ml": "", lata: "" },
                brand: "",
                alcohol: "",
                type: "",
                stock: "",
                size: "",
                quantity: "",
                active: true,
            });

            fetchData();
        } catch (err) {
            setError("Failed to save edit: " + err.message);
        }
    };

    const handleDeleteItem = async (id, categoryName) => {
        try {
            const collectionName = getCollectionName(categoryName);
            const docRef = doc(db, "Restaurants", "AlmacenGDL", collectionName, id);
            await deleteDoc(docRef);
            fetchData();
        } catch (err) {
            setError("Failed to delete item: " + err.message);
        }
    };

    const handleToggleActive = async (id, categoryName, currentActive) => {
        try {
            const collectionName = getCollectionName(categoryName);
            const docRef = doc(db, "Restaurants", "AlmacenGDL", collectionName, id);
            await updateDoc(docRef, {
                active: !currentActive,
                timestamp: serverTimestamp(),
            });
            fetchData();
        } catch (err) {
            setError("Failed to toggle active state: " + err.message);
        }
    };

    const calculateStats = () => {
        let totalItems = 0;
        let activeItems = 0;
        let totalValue = 0;
        let inactiveItems = 0;

        Object.values(allItems).forEach(categoryItems => {
            categoryItems.forEach(item => {
                totalItems++;
                if (item.active) {
                    activeItems++;
                    if (item.price) {
                        totalValue += item.price;
                    } else if (item.prices) {
                        // For beers, use average price
                        const prices = Object.values(item.prices).filter(p => p);
                        if (prices.length > 0) {
                            totalValue += prices.reduce((sum, price) => sum + price, 0) / prices.length;
                        }
                    }
                } else {
                    inactiveItems++;
                }
            });
        });

        return { totalItems, activeItems, inactiveItems, totalValue };
    };

    const renderFormFields = () => {
        if (newItem.category === "Cervezas") {
            return (
                <>
                    <input
                        type="text"
                        value={newItem.name}
                        onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                        placeholder="Nombre de la cerveza"
                        className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                    />
                    <input
                        type="text"
                        value={newItem.brand}
                        onChange={(e) => setNewItem({ ...newItem, brand: e.target.value })}
                        placeholder="Marca"
                        className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                    />
                    <input
                        type="text"
                        value={newItem.type}
                        onChange={(e) => setNewItem({ ...newItem, type: e.target.value })}
                        placeholder="Tipo (IPA, Lager, etc.)"
                        className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                        type="text"
                        value={newItem.alcohol}
                        onChange={(e) => setNewItem({ ...newItem, alcohol: e.target.value })}
                        placeholder="Alcohol % (ej: 5.0% ALC. VOL.)"
                        className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                    />
                    <input
                        type="number"
                        step="0.01"
                        value={newItem.prices["200ml"]}
                        onChange={(e) => setNewItem({ ...newItem, prices: { ...newItem.prices, "200ml": e.target.value } })}
                        placeholder="Precio 200ml"
                        className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                        type="number"
                        step="0.01"
                        value={newItem.prices["400ml"]}
                        onChange={(e) => setNewItem({ ...newItem, prices: { ...newItem.prices, "400ml": e.target.value } })}
                        placeholder="Precio 400ml"
                        className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                        type="number"
                        step="0.01"
                        value={newItem.prices.lata}
                        onChange={(e) => setNewItem({ ...newItem, prices: { ...newItem.prices, lata: e.target.value } })}
                        placeholder="Precio Lata"
                        className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                </>
            );
        } else {
            return (
                <>
                    <input
                        type="text"
                        value={newItem.name}
                        onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                        placeholder="Nombre del producto"
                        className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                    />
                    <input
                        type="number"
                        step="0.01"
                        value={newItem.price}
                        onChange={(e) => setNewItem({ ...newItem, price: e.target.value })}
                        placeholder="Precio"
                        className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                    />
                    {newItem.category === "Bebidas" ? (
                        <input
                            type="text"
                            value={newItem.size}
                            onChange={(e) => setNewItem({ ...newItem, size: e.target.value })}
                            placeholder="Tamaño (ej: 350 ML)"
                            className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    ) : (
                        <input
                            type="text"
                            value={newItem.weight}
                            onChange={(e) => setNewItem({ ...newItem, weight: e.target.value })}
                            placeholder="Peso (ej: 175g)"
                            className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    )}
                    {(newItem.category === "Entradas" || newItem.category === "Fuertes") && (
                        <input
                            type="text"
                            value={newItem.quantity}
                            onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
                            placeholder="Cantidad (ej: 5 Pzas)"
                            className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    )}
                    {newItem.category === "Merch" && (
                        <input
                            type="number"
                            value={newItem.stock}
                            onChange={(e) => setNewItem({ ...newItem, stock: e.target.value })}
                            placeholder="Stock disponible"
                            className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    )}
                    <textarea
                        value={newItem.description}
                        onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                        placeholder="Descripción"
                        className="p-3 border border-gray-300 rounded-lg col-span-2 focus:outline-none focus:ring-2 focus:ring-blue-500 h-20"
                    />
                </>
            );
        }
    };

    if (loading) return (
        <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
                <Beer className="h-12 w-12 text-gray-400 mx-auto animate-pulse mb-4" />
                <p className="text-gray-600">Cargando menú...</p>
            </div>
        </div>
    );

    if (error) return (
        <div className="flex items-center justify-center min-h-screen">
            <div className="text-center p-6 bg-red-50 rounded-lg border border-red-200">
                <p className="text-red-600 font-medium">{error}</p>
                <button
                    onClick={fetchData}
                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                    Reintentar
                </button>
            </div>
        </div>
    );

    const stats = calculateStats();

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            {/* Header */}
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 py-6">
                    <div className="flex items-center justify-center space-x-4 mb-6">
                        <Beer className="h-10 w-10 text-amber-600" />
                        <h1 className="text-3xl font-bold text-gray-800 uppercase tracking-wide">
                            Almacén Dashboard
                        </h1>
                    </div>

                    {/* Statistics */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-blue-100 text-sm">Total Productos</p>
                                    <p className="text-2xl font-bold">{stats.totalItems}</p>
                                </div>
                                <Package className="h-8 w-8 text-blue-200" />
                            </div>
                        </div>

                        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-4 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-green-100 text-sm">Activos</p>
                                    <p className="text-2xl font-bold">{stats.activeItems}</p>
                                </div>
                                <Eye className="h-8 w-8 text-green-200" />
                            </div>
                        </div>

                        <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-lg p-4 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-red-100 text-sm">Inactivos</p>
                                    <p className="text-2xl font-bold">{stats.inactiveItems}</p>
                                </div>
                                <EyeOff className="h-8 w-8 text-red-200" />
                            </div>
                        </div>

                        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-4 text-white">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-purple-100 text-sm">Valor Promedio</p>
                                    <p className="text-2xl font-bold">${(stats.totalValue / stats.activeItems || 0).toFixed(0)}</p>
                                </div>
                                <DollarSign className="h-8 w-8 text-purple-200" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 py-8">
                {/* Add/Edit Form */}
                <div className="mb-8 bg-white shadow-lg rounded-xl border border-gray-200 overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
                        <h2 className="text-xl font-semibold text-white flex items-center">
                            <Plus className="mr-2" />
                            {editItemId ? "Editar Producto" : "Agregar Nuevo Producto"}
                        </h2>
                    </div>

                    <form onSubmit={handleAddItem} className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                            <select
                                value={newItem.category}
                                onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                                className="p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                required
                                disabled={editItemId}
                            >
                                <option value="">Seleccionar Categoría</option>
                                {categories.map(cat => (
                                    <option key={cat.name} value={cat.name}>{cat.name}</option>
                                ))}
                            </select>

                            {renderFormFields()}
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3">
                            {!editItemId ? (
                                <button
                                    type="submit"
                                    className="flex-1 bg-blue-600 text-white p-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center font-medium"
                                >
                                    <Plus className="mr-2 h-5 w-5" />
                                    Agregar Producto
                                </button>
                            ) : (
                                <div className="flex gap-3 w-full">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setEditItemId(null);
                                            setEditCategory(null);
                                            setNewItem({
                                                category: "",
                                                name: "",
                                                price: "",
                                                weight: "",
                                                description: "",
                                                prices: { "200ml": "", "400ml": "", lata: "" },
                                                brand: "",
                                                alcohol: "",
                                                type: "",
                                                stock: "",
                                                size: "",
                                                quantity: "",
                                                active: true,
                                            });
                                        }}
                                        className="flex-1 px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center"
                                    >
                                        <X className="mr-2 h-5 w-5" />
                                        Cancelar
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSaveEdit}
                                        className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center"
                                    >
                                        <Save className="mr-2 h-5 w-5" />
                                        Guardar
                                    </button>
                                </div>
                            )}
                        </div>
                    </form>
                </div>

                {/* Menu Categories */}
                <div className="grid gap-6">
                    {categories.map((category) => {
                        const categoryItems = allItems[category.name] || [];
                        const IconComponent = category.icon;

                        return (
                            <div key={category.name} className={`bg-white shadow-lg rounded-xl border-2 ${category.color} overflow-hidden`}>
                                <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-white to-gray-50">
                                    <h2 className="text-2xl font-bold text-gray-800 uppercase tracking-wide flex items-center">
                                        <IconComponent className="mr-3 h-6 w-6" />
                                        {category.name}
                                        <span className="ml-3 bg-gray-200 text-gray-700 text-sm px-3 py-1 rounded-full">
                                            {categoryItems.length}
                                        </span>
                                        <span className="ml-2 bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full">
                                            {categoryItems.filter(item => item.active).length} activos
                                        </span>
                                    </h2>
                                </div>

                                <div className="p-6">
                                    {categoryItems.length === 0 ? (
                                        <p className="text-gray-500 text-center py-8 italic">
                                            No hay productos en esta categoría
                                        </p>
                                    ) : (
                                        <div className="space-y-3">
                                            {categoryItems.map((item) => (
                                                <div key={item.id} className={`border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow ${!item.active ? 'bg-gray-100 opacity-75' : 'bg-gray-50'}`}>
                                                    <div className="flex justify-between items-start">
                                                        <div className="flex-1">
                                                            {category.name === "Cervezas" ? (
                                                                <div>
                                                                    <span className="font-bold text-gray-800 text-lg">
                                                                        {item.name}
                                                                    </span>
                                                                    {item.type && (
                                                                        <span className="text-gray-500 italic ml-2 text-sm">
                                                                            {item.type}
                                                                        </span>
                                                                    )}
                                                                    <div className="text-gray-600 mt-1">
                                                                        <span className="font-medium">{item.brand}</span>
                                                                        {item.alcohol && <span className="ml-2">({item.alcohol})</span>}
                                                                    </div>
                                                                    {item.prices && Object.keys(item.prices).length > 0 && (
                                                                        <div className="mt-1 text-sm text-gray-600">
                                                                            Precios: {Object.entries(item.prices).map(([size, price]) =>
                                                                                `${size}: $${price}`
                                                                            ).join(' | ')}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <div>
                                                                    <span className="font-bold text-gray-800 text-lg">
                                                                        {item.name}
                                                                    </span>
                                                                    {(item.weight || item.size || item.quantity) && (
                                                                        <span className="text-gray-600 ml-2">
                                                                            ({item.weight || item.size || item.quantity})
                                                                        </span>
                                                                    )}
                                                                    <span className="text-green-600 font-semibold ml-2">
                                                                        ${item.price}
                                                                    </span>
                                                                    {category.name === "Merch" && item.stock !== undefined && (
                                                                        <span className="text-blue-600 ml-2">
                                                                            | Stock: {item.stock}
                                                                        </span>
                                                                    )}
                                                                    {item.description && (
                                                                        <div className="mt-1 text-sm text-gray-600">
                                                                            {item.description}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div className="flex items-center space-x-2 ml-4">
                                                            <button
                                                                onClick={() => handleEditItem(item)}
                                                                className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded-lg transition-colors"
                                                                title="Editar"
                                                            >
                                                                <Edit2 size={18} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteItem(item.id, category.name)}
                                                                className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded-lg transition-colors"
                                                                title="Eliminar"
                                                            >
                                                                <Trash2 size={18} />
                                                            </button>
                                                            <button
                                                                onClick={() => handleToggleActive(item.id, category.name, item.active)}
                                                                className={`p-2 rounded-lg transition-colors ${item.active
                                                                        ? "text-green-600 hover:text-green-800 hover:bg-green-50"
                                                                        : "text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                                                                    }`}
                                                                title={item.active ? "Desactivar" : "Activar"}
                                                            >
                                                                {item.active ? <CheckSquare size={18} /> : <Square size={18} />}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Footer */}
                <div className="mt-8 text-center bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                    <p className="text-gray-500 text-sm italic">
                        * Si tienes alergias, favor de preguntar los ingredientes.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;